import {
  Component,
  ChangeDetectionStrategy,
  input,
  signal,
  computed,
  effect,
  inject,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import {
  faBullhorn,
  faCalendarAlt,
  faUser,
  faGraduationCap,
} from "@fortawesome/free-solid-svg-icons";

import { CallForOpportunity } from "src/app/api/models/call-for-opportunity";
import { CallForOpportunityPublish } from "src/app/api/models/call-for-opportunity-publish";
import { ProfileLookingFor } from "src/app/api/models/profile-looking-for";
import { LocaleService } from "src/app/shared/services/locale.service";
import { ProfileLookingForUtils } from "src/app/shared/utils/student-profile.utils";
import { NetworkCardComponent } from "src/app/shared/components/network-card/network-card.component";
import { NetworkCardConfig, CardBadge } from "src/app/shared/components/network-card/network-card.types";

type OpportunityData = CallForOpportunity | CallForOpportunityPublish;

/**
 * Call-for-opportunity card wrapper that configures NetworkCardComponent.
 * Follows the same pattern as job-card, student-card, and company-card.
 */
@Component({
  selector: "app-call-for-opportunity-card",
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    FontAwesomeModule,
    NetworkCardComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: "flex grow w-full min-w-0" },
  template: `<app-network-card [config]="cardConfig()" [entity]="entity()" />`,
})
export class CallForOpportunityCardComponent {
  data = input.required<OpportunityData>();

  private localeService = inject(LocaleService);

  entity = signal<OpportunityData>({});

  constructor() {
    effect(() => {
      const data = this.data();
      if (data) {
        this.entity.set(data);
      }
    });
  }

  cardConfig = computed<NetworkCardConfig<OpportunityData>>(() => ({
    getId: (d) => (d as CallForOpportunity).id ?? (d as CallForOpportunity).studentProfileId,
    getTitle: (d) => this.getDisplayName(d),
    getAvatarUri: () => undefined,
    getCoverPhotoUri: () => undefined,
    getRouterLink: (d) => {
      const profileId = (d as CallForOpportunity).studentProfileId;
      return profileId ? `/company/students/${profileId}` : "";
    },
    avatarPlaceholder: faUser,
    fields: [
      {
        icon: faCalendarAlt,
        getValue: (d) =>
          d.startDateTime ? this.formatDate(d.startDateTime) : undefined,
        hideIfEmpty: true,
      },
      {
        icon: faCalendarAlt,
        getValue: (d) =>
          d.endDateTime ? this.formatDate(d.endDateTime) : "-",
        hideIfEmpty: false,
      },
      {
        icon: faGraduationCap,
        getValue: (d) => d.highestEducation ?? undefined,
        hideIfEmpty: true,
      },
      {
        getValue: (d) => d.personalDescription ?? undefined,
        isExpandable: true,
        maxLength: 150,
        hideIfEmpty: true,
      },
    ],
    getBadges: (d) => {
      const badges: CardBadge[] = [];
      badges.push({
        text: this.getTranslationKey(d.lookingFor),
        colorClass: "badge-success",
        icon: faBullhorn,
      });
      return badges;
    },
    // No buttons — card itself is clickable via stretched-link
  }));

  private getDisplayName(d: OpportunityData): string {
    if ("firstName" in d && d.firstName) {
      return d.firstName;
    }
    if ("studentFullName" in d && d.studentFullName) {
      return d.studentFullName;
    }
    return "Anonymous Student";
  }

  private formatDate(dateString: string | undefined | null): string {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString(
        this.localeService.getCurrentLanguage()
      );
    } catch {
      return dateString;
    }
  }

  private getTranslationKey(lookingFor: string | null | undefined): string {
    if (!lookingFor) return "Not actively looking";
    return ProfileLookingForUtils.getTranslationKey(
      lookingFor as ProfileLookingFor
    );
  }
}
